import type { ComponentChildren, JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Card, CardBody, CardHeader } from "@gss/components/Card";
import { Col } from "@gss/components/Col";
import { Collapse, Expand, Help } from "@gss/components/icons";
import { Label } from "@gss/components/Label";
import { Row } from "@gss/components/Row";
import type {
    Filter,
    MeasurementStat,
    OrgStructure,
} from "@gss/types/measurementStat";
import type { SecurityContext } from "@gss/types/SecurityContext";
import type { StringRecord } from "@gss/types/StringRecord";
import type { Selected, SelectedData } from "../types";
import { useTranslation } from "../util/Translation";
import { Filter as FilterComponent } from "./Filter";
import "./Filters.css";
import { OrgFilter } from "./OrgFilter";

interface Props {
    stat: MeasurementStat;
    audience: SecurityContext;
    managerOrganistions: string[];
    setSelectedData: (data: SelectedData) => void;
}

export function Filters({
    stat,
    audience,
    managerOrganistions,
    setSelectedData,
}: Props): JSX.Element | null {
    const [orgStructure, setOrgStructure] = useState(
        internalValueOrFirst(stat.orgStructures),
    );
    const [selected, setSelected] = useState<Record<string, Selected[]>>();
    const [multiSelectValue, setMultiSelectValue] = useState<string>();
    const [open, setOpen] = useState(true);
    const t = useTranslation().translation.filters;
    const isPublic = audience === "public";

    useEffect(() => {
        if (selected != null) {
            setSelectedData({ orgStructure, selected });
        }
    }, [orgStructure, selected, t]);

    useEffect(() => {
        const hashParameters = getHashParameters();

        const selected: Record<string, Selected[]> = {};
        for (const f of stat.filters) {
            if (f.lockedOption == null) {
                const selectedOptionValues = hashParameters[f.value];
                selected[f.value] = FilterComponent.getDefaultSelected(
                    f,
                    selectedOptionValues,
                );
            }
        }
        if (orgStructure.organisations.length > 1) {
            const selectedOptionValues = hashParameters[OrgFilter.value] ?? [];
            if (selectedOptionValues.length > 0) {
                selected[OrgFilter.value] = OrgFilter.getDefaultSelected(
                    orgStructure,
                    selectedOptionValues,
                    false,
                );
            } else {
                selected[OrgFilter.value] = OrgFilter.getDefaultSelected(
                    orgStructure,
                    managerOrganistions,
                    true,
                );
            }
        }
        setSelected(selected);
    }, []);

    if (selected == null) {
        return null;
    }

    const addSelected = (filterValue: string, selection: Selected) => {
        const list = selected[filterValue];
        list.push(selection);
        setSelected({
            ...selected,
            [filterValue]: [...list],
        });
    };

    const removeSelected = (filterValue: string, key: string) => {
        const list = selected[filterValue];
        list.splice(
            list.findIndex((s) => s.key === key),
            1,
        );
        setSelected({
            ...selected,
            [filterValue]: [...list],
        });
    };

    const setFilterSelected = (
        filterValue: string,
        selectedOptions: Selected[],
    ) => {
        setSelected({
            ...selected,
            [filterValue]: selectedOptions,
        });
    };

    const renderFilters = (title: ComponentChildren, filters: Filter[]) => {
        if (filters.length === 0) {
            return null;
        }

        return (
            <>
                <hr />

                <Label className="fst-italic">{title}</Label>

                <Row>
                    {filters.map((f) => (
                        <Col key={f.value} xs="4">
                            <FilterComponent
                                filter={f}
                                selected={selected[f.value]}
                                isPublic={isPublic}
                                addSelected={addSelected}
                                removeSelected={removeSelected}
                                setSelected={setFilterSelected}
                                multiSelectValue={multiSelectValue}
                                setMultiSelectValue={setMultiSelectValue}
                            />
                        </Col>
                    ))}
                </Row>
            </>
        );
    };

    const renderBody = () => {
        const metadataBasedFilters = stat.filters.filter(
            (f) => f.lockedOption == null && !f.basedOnQuestion,
        );
        const questionBasedFilters = stat.filters.filter(
            (f) => f.lockedOption == null && f.basedOnQuestion,
        );

        return (
            <>
                <p className="fst-italic">{t.bookmarkDescription}</p>

                {orgStructure.organisations.length > 1 && (
                    <OrgFilter
                        orgStructures={stat.orgStructures}
                        orgStructure={orgStructure}
                        setOrgStructure={setOrgStructure}
                        isPublic={isPublic}
                        managerOrganistions={managerOrganistions}
                        selected={selected[OrgFilter.value]}
                        addSelected={addSelected}
                        removeSelected={removeSelected}
                        setSelected={setFilterSelected}
                        multiSelectValue={multiSelectValue}
                        setMultiSelectValue={setMultiSelectValue}
                    />
                )}

                {renderFilters(t.basedOnMetadataFilters, metadataBasedFilters)}

                {renderFilters(
                    <span title={t.basedOnQuestionDesc}>
                        {t.basedOnQuestionFilters} <Help aria-hidden />
                    </span>,
                    questionBasedFilters,
                )}
            </>
        );
    };

    return (
        <Card className="filters">
            <CardHeader aria-expanded={open} onClick={() => setOpen(!open)}>
                <h2 className="h3 mb-0">
                    <small>
                        {open ? (
                            <Collapse className="align-baseline" />
                        ) : (
                            <Expand className="align-baseline" />
                        )}
                    </small>
                    &nbsp;
                    {t.filters}
                </h2>
            </CardHeader>
            {open && <CardBody>{renderBody()}</CardBody>}
        </Card>
    );
}

function internalValueOrFirst(orgStructures: OrgStructure[]): OrgStructure {
    return (
        orgStructures.find((e) => e.value === "internal") ?? orgStructures[0]
    );
}

function getHashParameters(): StringRecord<string[]> {
    const results: StringRecord<string[]> = {};
    const parsedHash = new URLSearchParams(window.location.hash.slice(1));
    const filters = parsedHash.getAll("filter");
    for (const filter of filters) {
        const i = filter.indexOf(":");
        const key = filter.slice(0, i);
        const value = filter.slice(i + 1);
        results[key] ??= [];
        results[key].push(decodeURIComponent(value));
    }
    return results;
}
